import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import { useState, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

interface StripeElementHandlers {
  onFocus: () => void;
  onBlur: () => void;
}

interface StripeFieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  /** Render prop rather than a plain node: the Element has to receive the
   *  wrapper's focus handlers, and this is what hands them over without every
   *  caller wiring a no-op. */
  children: (handlers: StripeElementHandlers) => ReactNode;
  className?: string;
}

/**
 * MUI outlined-input chrome around a Stripe Element.
 *
 * A Stripe Element is an iframe: it cannot be placed inside `<TextField>`, and
 * it does not inherit focus or error styling from anything. This redraws the
 * border, label and helper text so a card field sits beside the cardholder-name
 * TextField without looking like a different form.
 *
 * The 44px min-height matches every other control in the app.
 */
export const StripeField = ({
  label,
  htmlFor,
  error,
  children,
  className,
}: StripeFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasError = error !== undefined && error !== '';

  return (
    <FormControl error={hasError} className={cn('w-full', className)}>
      <FormLabel htmlFor={htmlFor} className="mb-1 text-sm font-medium">
        {label}
      </FormLabel>

      <div
        className={cn(
          'flex min-h-11 w-full items-center rounded-lg border px-3 py-2.5 transition-colors',
          hasError
            ? 'border-error'
            : isFocused
              ? 'border-primary-dark ring-1 ring-primary-dark'
              : 'border-divider',
        )}
      >
        {/* Stripe sizes the iframe to its content; without this the element
            collapses to zero width inside a flex parent. */}
        <div className="min-w-0 flex-1">
          {children({
            onFocus: () => {
              setIsFocused(true);
            },
            onBlur: () => {
              setIsFocused(false);
            },
          })}
        </div>
      </div>

      {hasError && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
